import React, { useState, useEffect } from "react";
import { DateTime } from 'luxon';
import Toastr from 'toastr';
import Swal from 'sweetalert2';
import { Card, CardHeader, CardFooter, Col, Container, Row, Table, Alert, Spinner, Button } from "reactstrap";
import DaScreenHeader from "../components/Headers/DaScreenHeader";
import { Link, useHistory } from "react-router-dom";
import { Add, Visibility } from "@material-ui/icons";
import { useQueryParams } from '../hooks/useQueryParams';
import { useQueryClient } from 'react-query';
import { useGetAllConversation } from '../hooks/useGetAllConversation'
import { useNewConversationMutation } from '../hooks/useNewConversatoinMutation'
import { useConversationNameMutation } from '../hooks/useConversationNameMutation'
import CustomPagination from '../components/CustomPagination';
import { toCustomDateTimeString } from '../utils/toCustomDateTimeString';
import {DAChatType} from "../types"

const RESULTS_PER_PAGE = 10;

const DataAnalysis: React.FC = () => {
  const [chatName, setChatName] = useState<string>("")
  const [disableButton, setDisableButton] = useState<boolean>(false)

  const query = useQueryParams()
  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get('page') || 0)
    if (isNaN(page) || page <= 0) {
      return 1
    }
    return page
  })
  const queryClient = useQueryClient()
  const history = useHistory();

  const { mutate, data: NewConversation } = useNewConversationMutation((err, msg) => {
    if (err) {
      setDisableButton(false)
      Toastr.error(msg!)
    }
  })

  useEffect(() => {
    if (NewConversation) {
      history.push(`/admin/chat/${NewConversation?.id}`);
    }
  }, [NewConversation])

  const handleNewChatClick = async () => {
    const predefinedChatName = "Default Chat"
    setDisableButton(true)
    mutate({chatName: predefinedChatName, conversationType: DAChatType.CodeGen });
    setChatName(predefinedChatName);
  };

  const handleNewScreenerChatClick = async () => {
    const predefinedChatName = "Default Chat"
    setDisableButton(true)
    mutate({chatName: predefinedChatName, conversationType: DAChatType.ScreenerBot });
    setChatName(predefinedChatName);
  };


  const { mutate: mutateName } = useConversationNameMutation((err, msg) => {
    if (err) {
      Toastr.error(msg!)
    }
  })

  const handleEditChatName = (conversationId: string, index: number) => {
    const newChatName = Swal.fire({
      title: '<h2>Please enter your new Chat name:<h2>',
      input: 'text',
      inputValue: chatName,
      showCancelButton: true,
      inputValidator: (value: any) => {
        if (!value) {
          return 'You need to enter something!';
        }
      }
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        mutateName({ chatName: result.value, id: conversationId });

        if (data?.conversations) {
          data.conversations[index].name = result.value;
        }
        // window.location.reload();
      }
    });
  };

  const conversationName = ""
  const {
    isError,
    isLoading,
    data,
    refetch,
  } = useGetAllConversation(pageTable, RESULTS_PER_PAGE, conversationName)

  useEffect(() => {
    handleExecSearch()
  }, [pageTable])

  const handleExecSearch = () => {
    if (isLoading) {
      return
    }
    queryClient.removeQueries(["conversation", "all"])
    refetch()
  }

  let content: JSX.Element | null = null

  if (isError) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    );
  } else if (isLoading) {
    content = <Spinner />
  } else if (!data || data.conversations.length === 0) {
    content = (
      <Alert color="warning">
        No data available
      </Alert>
    );
  } else {
    content = (
      <>
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light">
            <tr>
              <th>Exec Id</th>
              <th>Name</th>
              <th>Conversation Type</th>
              <th>Executed By</th>
              <th>Executed At</th>
              <th>Cost</th>
              <th>Message count</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.conversations.map((conv, index) =>
              <tr key={conv.id}>
                <td>{conv.id}</td>
                <td>
                  <span className="text-sm" onClick={() => handleEditChatName(conv.id, index)} style={{ cursor: 'pointer' }}>
                    <i className='fas fa-edit mx-2' />
                  </span>
                  {conv?.name}
                </td>
                <td>{conv.conversationType}</td>
                <td>{conv?.user.name}</td>
                <td>{conv.createdAt ? toCustomDateTimeString(DateTime.fromISO(conv.createdAt)) : 'NA'}</td>
                <td>${conv?.cost}</td>
                <td>{conv?.messageCount}</td>
                <td>
                  <span className="status-light" style={{ backgroundColor: conv.containerStatus === 'running' ? 'green' : 'red' }} />
                  {conv?.containerStatus === 'running' ? "Active" : "Deactive"}
                </td>
                <td>
                  <Link to={`/admin/chat/${conv.id}`}><Visibility className="text-primary" /></Link>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <CardFooter className="py-4">
          <nav aria-label="...">
            <CustomPagination
              activePage={pageTable}
              totalResults={data.total}
              resultsPerPage={RESULTS_PER_PAGE}
              onChange={setPageTable}
              label="Table navigation"
            />
          </nav>
        </CardFooter>
      </>
    )
  }

  return (
    <>
      <DaScreenHeader />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h2>Data Cockpit</h2>
                  </Col>
                  <Col>
                    {disableButton ? (
                      <svg
                        style={{ float: 'right' }}
                        height="60px"
                        viewBox="0 0 187.3 93.7"
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <path
                          stroke="rgb(215, 61, 50)"
                          id="outline"
                          fill="none"
                          stroke-width="4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-miterlimit="10"
                          d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                        />
                        <path
                          id="outline-bg"
                          opacity="0.05"
                          fill="none"
                          stroke="#fff"
                          stroke-width="4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-miterlimit="10"
                          d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                        />
                      </svg>
                    ) : (
                      <h1 className="text-white">
                        <Button
                          style={{ float: 'right' }}
                          className="btn btn-sm btn-warning"
                          disabled={disableButton}
                          onClick={handleNewChatClick}
                        >
                          <Add />
                          &nbsp;Data Analysis
                        </Button>
                        <Button
                          style={{ float: 'right' }}
                          className="btn btn-sm mx-2"
                          disabled={disableButton}
                          onClick={handleNewScreenerChatClick}
                        >
                          <Add />
                          &nbsp;Screening
                        </Button>
                      </h1>
                    )}
                  </Col>
                </Row>
              </CardHeader>
              {content}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DataAnalysis;
